import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Divider, Popconfirm, Row, Table, message } from "antd";
import config from "app/envConfig";
import { camelToWords, formatDate, snakeToCamelCase } from "common/utils";
import {
  useDeleteCreditorDocumentMutation,
  useFetchCreditorDocumentsQuery,
} from "features/creditorProfile/creditorProfileAPI";
import UploadCreditorDocumentModal from "features/creditorProfileDocumentsTable/agencyPortal/uploadCreditorDocumentModal";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useState } from "react";
import styled from "styled-components";

const StyledDocumentButton = styled(Button)`
  padding-top: 0px;
  padding-bottom: 10px;
  line-height: 0px;
`;

const StyledHeader = styled.h4`
  margin-top: 0;
  margin-bottom: 36px;
  text-transform: uppercase;
`;

const StyledRow = styled(Row)`
  display: flex;
  width: 100%;
`;

const StyledDivider = styled(Divider)`
  position: absolute;
  top: 28px;
  left: 0;
  background-color: #f9f3f3;
`;

const StyledCard = styled(Card)`
  flex: 1;
  width: 100%;
  .ant-card-body {
    padding: 16px;
    overflow: visible;
  }
  overflow: visible;
  .ant-table-wrapper {
    width: 100%;
  }
  .ant-table {
    width: 100%;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-body {
    overflow-x: auto !important;
  }
`;

function CreditorProfileDocumentsTable({ currentCreditor }) {
  const { data: constants } = useFetchBackendConstantsQuery();
  const creditorId = currentCreditor?.id;
  const [isUploadDocumentModalVisible, setUploadDocumentModalVisible] = useState(false);
  const { data: documents, isLoading } = useFetchCreditorDocumentsQuery(
    {
      creditorId,
    },
    { skip: !creditorId },
  );
  const [deleteDocument] = useDeleteCreditorDocumentMutation();
  const {
    data: {
      [PERMISSIONS.DEBTOR_DOCUMENT__GET]: isDebtorDocumentGetAuthorized,
      [PERMISSIONS.DEBTOR_DOCUMENT__CREATE]: isDebtorDocumentCreateAuthorized,
      [PERMISSIONS.DEBTOR_DOCUMENT__DELETE]: isDebtorDocumentDeleteAuthorized,
      [PERMISSIONS.CLIENT_PROFILE__CAN_UPLOAD_DOCUMENTS]:
        isClientProfileCanUploadDocumentsAuthorized,
    },
  } = useAuthorizations();

  const handleDelete = async (payload) => {
    const result = await deleteDocument(payload);
    if ("data" in result) {
      message.success("Document removed successfully!");
    }
  };

  const onUploadDocumentOk = () => {
    setUploadDocumentModalVisible(false);
  };

  const onUploadDocumentCancel = () => {
    setUploadDocumentModalVisible(false);
  };

  const columns = [
    {
      title: "Upload Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => formatDate(record.createdAt),
      sorter: {
        compare: (a, b) => {
          const dateA = a.createdAt;
          const dateB = b.createdAt;
          if (dateA < dateB) {
            return -1;
          }
          if (dateA > dateB) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => {
        const prettifiedText = camelToWords(snakeToCamelCase(text));
        return <div>{prettifiedText}</div>;
      },
      sorter: (a, b) => {
        return a.type.toLowerCase() > b.type.toLowerCase();
      },
    },
    {
      title: "File Name",
      dataIndex: "filename",
      key: "filename",
      render: (text, record) => {
        return record.file ? (
          <a target="_blank" href={new URL(record.file, config.apiBaseUrl).href} rel="noreferrer">
            {text}
          </a>
        ) : (
          text
        );
      },
    },
    {
      title: "External URL",
      dataIndex: "externalUrl",
      key: "externalUrl",
      width: 200,
      render: (externalUrl) => {
        return externalUrl ? (
          <a target="_blank" href={externalUrl} rel="noreferrer">
            {externalUrl}
          </a>
        ) : null;
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
    },
    {
      title: "Created by",
      dataIndex: "createdByName",
      key: "createdByName",
      // check if user exists before rendering, otherwise render empty string
      render: (createdByName) => createdByName ?? "",
    },
    {
      title: "Data Source",
      dataIndex: "dataSource",
      render: (text, record) =>
        constants?.editableDataSourceTypes?.find((x) => x.value === record.dataSource)?.display ??
        "-",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      fixed: "right",
      key: "actions",
      hidden: !isDebtorDocumentDeleteAuthorized,
      render: (text, record) => {
        return (
          <Popconfirm
            placement="topLeft"
            okText="Yes"
            title="Are you sure you want to delete this document?"
            onConfirm={() => handleDelete({ documentId: record.id, creditorId })}
          >
            <DeleteOutlined key="delete" />
          </Popconfirm>
        );
      },
    },
  ].filter((column) => !column.hidden);

  return isDebtorDocumentGetAuthorized ? (
    <>
      <StyledCard>
        <StyledRow>
          <StyledHeader>Documents</StyledHeader>
          {isDebtorDocumentCreateAuthorized && (
            <StyledDocumentButton
              type="link"
              icon={<PlusOutlined />}
              onClick={() => setUploadDocumentModalVisible(true)}
            >
              Add Document
            </StyledDocumentButton>
          )}
          <StyledDivider />
        </StyledRow>
        <StyledTable
          // @ts-ignore
          columns={columns}
          loading={isLoading}
          bordered
          scroll={{ x: true }}
          dataSource={documents}
          pagination={{ pageSize: 100 }}
        />
      </StyledCard>
      {isUploadDocumentModalVisible && (
        <UploadCreditorDocumentModal
          creditorId={creditorId}
          title="Add Document"
          open={isUploadDocumentModalVisible}
          onOk={onUploadDocumentOk}
          onCancel={onUploadDocumentCancel}
        />
      )}
    </>
  ) : null;
}

export default CreditorProfileDocumentsTable;
